/**
 * @typedef {{
 * 	dsn: string;
 * 	release: string;
 * 	environment?: string;
 * 	tags?: Record<string, string>;
 * 	isExpectedError?: (e: unknown) => boolean;
 * }} SentryConfig
 */

/**
 * @type {{
 * 	key: string;
 * 	host: string;
 * 	projectId: string;
 * 	dsn: string;
 * 	release: string;
 * 	environment: string;
 * 	tags: Record<string, string>;
 * 	isExpectedError?: (e: unknown) => boolean;
 * } | null}
 */
let config = null;

/** @type {WeakSet<object>} */
const reported = new WeakSet();

/**
 * Initialize Sentry with the given configuration.
 *
 * @param {SentryConfig} options
 */
export function init(options) {
	const url = URL.parse(options.dsn);
	if (!url) return;

	const projectId = url.pathname.slice(1);
	const key = url.username;
	const host = url.host;
	if (!projectId || !key || !host) return;

	config = {
		key,
		host,
		projectId,
		dsn: options.dsn,
		release: options.release,
		environment: options.environment || "production",
		tags: options.tags || {},
		isExpectedError: options.isExpectedError,
	};

	window.onerror = (_evt, _source, _line, _col, err) => {
		captureError(err ?? new Error(String(_evt)));
	};

	window.onunhandledrejection = evt => {
		captureError(evt.reason, "UnhandledRejection");
	};
}

/**
 * Set a tag on all future Sentry events.
 *
 * @param {string} key
 * @param {string} value
 */
export function setTag(key, value) {
	if (!config) return;
	config.tags[key] = value;
}

/**
 * Report an error to Sentry, typed by the error's name. Thrown values that
 * aren't errors are typed by the mechanism that caught them.
 *
 * @param {unknown} error
 * @param {"Error" | "UnhandledRejection"} [mechanism]
 * @param {Record<string, unknown>} [extra]
 */
export function captureError(error, mechanism = "Error", extra) {
	if (!config) return;
	if (error instanceof Object) {
		if (reported.has(error)) return;
		reported.add(error);
	}
	if (config.isExpectedError?.(error)) return;

	const type = error instanceof Error ? error.name : mechanism;
	const message = error instanceof Error ? error.message : String(error);
	const stack = error instanceof Error ? error.stack : undefined;

	const eventId = crypto.randomUUID().replace(/-/g, "");
	const timestamp = Date.now() / 1000;

	/** @type {Record<string, unknown>} */
	const event = {
		event_id: eventId,
		timestamp,
		platform: "javascript",
		environment: config.environment,
		release: config.release,
		tags: config.tags,
		contexts: { browser: { name: navigator.userAgent } },
		request: { url: location.href },
		extra,
		exception: {
			values: [
				{
					type,
					value: message,
					stacktrace: stack ? parseFrames(stack) : undefined,
					mechanism: { type: mechanism, handled: false },
				},
			],
		},
	};

	const header = JSON.stringify({
		event_id: eventId,
		sent_at: new Date().toISOString(),
		dsn: config.dsn,
	});
	const itemHeader = JSON.stringify({ type: "event" });
	const envelope = `${header}\n${itemHeader}\n${JSON.stringify(event)}`;

	fetch(`https://${config.host}/api/${config.projectId}/envelope/`, {
		method: "POST",
		headers: {
			"Content-Type": "application/x-sentry-envelope",
			"X-Sentry-Auth": `Sentry sentry_key=${config.key}, sentry_version=7`,
		},
		body: envelope,
	}).catch(() => {});
}

/**
 * @typedef {object} StackFrame
 * @property {string} filename
 * @property {string} abs_path
 * @property {number} lineno
 * @property {number} colno
 * @property {string} function
 * @property {boolean} in_app
 */

/**
 * @param {string} stack
 * @returns {StackFrame[]}
 */
/**
 * @param {string} stack
 * @returns {{ frames: StackFrame[] } | undefined}
 */
function parseFrames(stack) {
	const frames = parseStack(stack);
	return frames.length > 0 ? { frames } : undefined;
}

/**
 * @param {string} stack
 * @returns {StackFrame[]}
 */
function parseStack(stack) {
	/** @type {StackFrame[]} */
	const frames = [];
	for (const line of stack.split("\n")) {
		// Chrome: "at func (url:line:col)" / Firefox: "func@url:line:col"
		const match = line.match(
			/(?:at\s+(?:(.+?)\s+\()?(.+?):(\d+):(\d+)\)?|(.+?)@(.+?):(\d+):(\d+))/,
		);
		const fn = match?.[1] ?? match?.[5];
		const absPath = match?.[2] ?? match?.[6];
		const lineno = match?.[3] ?? match?.[7];
		const colno = match?.[4] ?? match?.[8];
		if (absPath && lineno && colno) {
			const url = URL.parse(absPath);
			frames.unshift({
				function: fn || "<anonymous>",
				filename: url ? url.pathname : absPath,
				abs_path: absPath,
				lineno: parseInt(lineno, 10),
				colno: parseInt(colno, 10),
				in_app: url ? url.origin === location.origin : false,
			});
		}
	}
	return frames;
}
